import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, MessageSquare, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { MpesaPaymentSection } from '@/features/checkout/components/mpesa-payment-section';
import { supabase } from '@/lib/supabase';
import {
  getContactDisplayNumber,
  getContactWhatsappTarget,
  type SupportContact,
} from '@/features/settings/support-contacts';
import { useSettings } from '@/features/settings/hooks/use-settings';
import { renderSmsTemplate, WHATSAPP_ORDER_MESSAGE_DEFAULT } from '@/features/sms/config';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
}

interface OrderDetails {
  id: string;
  order_number?: string | number;
  order_reference?: string;
  branch_id?: string;
  customer_name?: string;
  customer_phone?: string;
  delivery_address?: string;
  total_amount?: number;
  items?: OrderItem[];
}

interface BranchConfig {
  mpesa_payment_type: 'buy_goods' | 'stk_push' | 'both';
  mpesa_shortcode: string;
}

type PostOrderAction = 'payment' | 'call' | 'sms' | 'whatsapp';

const ACTION_LABELS: Record<PostOrderAction, string> = {
  payment: 'Continue to payment',
  call: 'Call the store',
  sms: 'Send an SMS',
  whatsapp: 'Open WhatsApp',
};

function normalizePostOrderAction(value: string | null): PostOrderAction {
  switch (value) {
    case 'call':
    case 'sms':
    case 'whatsapp':
      return value;
    default:
      return 'payment';
  }
}

function formatOrderLabel(order: OrderDetails | null, orderReferenceFromQuery: string | null): string {
  if (orderReferenceFromQuery) {
    return orderReferenceFromQuery;
  }

  if (order?.order_reference) {
    return order.order_reference;
  }

  if (order?.order_number !== undefined) {
    return `KRB-${String(order.order_number).padStart(6, '0')}`;
  }

  return order?.id ? order.id.slice(-8).toUpperCase() : 'Pending';
}

function buildOrderMessage(order: OrderDetails | null, orderLabel: string, template?: string): string {
  const itemsSummary = (order?.items || [])
    .map((item) => `${item.quantity}x ${item.product_name}`)
    .join(', ');

  const amountSummary = order?.total_amount ? ` worth KES ${Number(order.total_amount).toLocaleString()}` : '';

  const messageTemplate = template || WHATSAPP_ORDER_MESSAGE_DEFAULT;

  return renderSmsTemplate(messageTemplate, {
    order_reference: orderLabel,
    items_summary: itemsSummary || 'my items',
    amount_summary: amountSummary,
    customer_name: order?.customer_name || '',
  });
}

export default function OrderConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const action = normalizePostOrderAction(searchParams.get('action'));
  const orderReferenceFromQuery = searchParams.get('orderReference');
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [branchConfig, setBranchConfig] = useState<BranchConfig>({
    mpesa_payment_type: 'buy_goods',
    mpesa_shortcode: '3022071',
  });
  const { settings } = useSettings();

  const supportContacts = settings.support_numbers;
  const defaultSupportContact = supportContacts.find((contact) => contact.isDefault) || supportContacts[0] || null;

  useEffect(() => {
    if (!selectedContactId && defaultSupportContact) {
      setSelectedContactId(defaultSupportContact.id);
    }
  }, [defaultSupportContact, selectedContactId]);

  const selectedContact = useMemo<SupportContact | null>(() => {
    if (supportContacts.length === 0) {
      return null;
    }

    return supportContacts.find((contact) => contact.id === selectedContactId) || defaultSupportContact;
  }, [defaultSupportContact, selectedContactId, supportContacts]);

  useEffect(() => {
    async function loadOrder() {
      if (!orderId) {
        setError('Order ID is missing.');
        setIsLoading(false);
        return;
      }

      if (!supabase) {
        setError('Supabase is not configured.');
        setIsLoading(false);
        return;
      }

      try {
        const [orderResult, itemsResult] = await Promise.all([
          supabase
            .from('orders')
            .select('id, order_number, order_reference, branch_id, customer_name, customer_phone, delivery_address, total_amount')
            .eq('id', orderId)
            .single(),
          supabase
            .from('order_items')
            .select('id, product_name, quantity, unit_price')
            .eq('order_id', orderId)
            .order('created_at', { ascending: true }),
        ]);

        if (orderResult.error) {
          throw orderResult.error;
        }

        if (itemsResult.error) {
          throw itemsResult.error;
        }

        if (!orderResult.data) {
          throw new Error('Order not found');
        }

        setOrder({
          ...(orderResult.data as OrderDetails),
          items: (itemsResult.data as OrderItem[] | null) || [],
        });
      } catch (fetchError) {
        setError(fetchError instanceof Error ? fetchError.message : 'Failed to load order');
      } finally {
        setIsLoading(false);
      }
    }

    loadOrder();
  }, [orderId]);

  useEffect(() => {
    if (!supabase || !order?.branch_id || action !== 'payment') {
      return;
    }

    const query = supabase
      .from('branches')
      .select('mpesa_payment_type, mpesa_shortcode')
      .eq('id', order.branch_id)
      .single();

    query.then(({ data, error: queryError }: { data: BranchConfig | null; error: unknown }) => {
      if (!queryError && data) {
        setBranchConfig(data);
      }
    });
  }, [action, order?.branch_id]);

  const orderLabel = formatOrderLabel(order, orderReferenceFromQuery);
  const actionMessage = buildOrderMessage(order, orderLabel, settings.whatsapp_order_message_template);
  const selectedWhatsappTarget = getContactWhatsappTarget(selectedContact);
  const selectedPhone = selectedContact?.phone || '';
  const selectedDisplayPhone = getContactDisplayNumber(selectedContact);

  const handleCall = () => {
    if (selectedPhone) {
      window.location.href = `tel:${selectedPhone}`;
    }
  };

  const handleSms = () => {
    if (selectedPhone) {
      window.location.href = `sms:${selectedPhone}?body=${encodeURIComponent(actionMessage)}`;
    }
  };

  const handleWhatsApp = () => {
    if (selectedWhatsappTarget) {
      window.open(`https://wa.me/${selectedWhatsappTarget}?text=${encodeURIComponent(actionMessage)}`, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading your order...</p>
      </div>
    );
  }

  if (error || !orderId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <h1 className="text-xl font-semibold">Order not available</h1>
            <p className="text-gray-500">{error || 'We could not load your order.'}</p>
            <Button onClick={() => navigate('/cart')}>Back to Cart</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Order Created</h1>
            <p className="text-sm text-gray-500">
              Your order exists now. Next step: {ACTION_LABELS[action].toLowerCase()} with a valid order reference.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Order {orderLabel}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-medium">{order.customer_name || order.customer_phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Delivery address</p>
                <p className="font-medium">{order.delivery_address || 'Not provided'}</p>
              </div>
            </div>

            {(order.items || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Items</p>
                {(order.items || []).map((item) => (
                  <div key={item.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span>{item.quantity}x {item.product_name}</span>
                    <span>KES {(item.quantity * item.unit_price).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg bg-brand-50 px-4 py-3">
              <span className="font-medium text-brand-900">Total</span>
              <span className="text-lg font-bold text-brand-900">
                {order.total_amount ? `KES ${Number(order.total_amount).toLocaleString()}` : 'Pending'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Support Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {supportContacts.length > 0 ? (
              <>
                <Select
                  label="Use this number for follow-up"
                  value={selectedContact?.id || ''}
                  onValueChange={setSelectedContactId}
                  options={supportContacts.map((contact) => ({
                    value: contact.id,
                    label: `${contact.label} - ${getContactDisplayNumber(contact)}`,
                  }))}
                />
                <p className="text-sm text-gray-500">
                  Selected contact: {selectedContact?.label || 'Not selected'}{selectedDisplayPhone ? ` (${selectedDisplayPhone})` : ''}
                </p>
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                No support numbers are configured right now. The order is saved, but call and text actions are disabled until a support number is added in admin settings.
              </div>
            )}
          </CardContent>
        </Card>

        {action === 'payment' && (
          <MpesaPaymentSection
            amount={Number(order.total_amount || 0)}
            orderId={order.id}
            branchId={order.branch_id}
            paymentType={branchConfig.mpesa_payment_type}
            tillNumber={branchConfig.mpesa_shortcode}
            supportPhone={selectedPhone}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>Post-Order Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <Button
              onClick={handleCall}
              disabled={!selectedPhone}
              className="gap-2"
              variant={action === 'call' ? 'default' : 'outline'}
            >
              <Phone className="h-4 w-4" />
              Call Store
            </Button>
            <Button
              onClick={handleSms}
              disabled={!selectedPhone}
              variant={action === 'sms' ? 'default' : 'outline'}
              className="gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              Send SMS
            </Button>
            <Button
              onClick={handleWhatsApp}
              disabled={!selectedWhatsappTarget}
              variant={action === 'whatsapp' ? 'default' : 'outline'}
              className="gap-2"
            >
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Message Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{actionMessage}</p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
